import UUID from 'util/UUID';
import { BuildingId, BuildingOrg, DeviceType, Schedule, ScheduleRule } from 'shared/models';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScheduleRulePanel } from '../scheduleRulePanel/ScheduleRulePanel';
import { isEqual } from 'lodash';
import { useBeforeUnload } from 'ui/shared/useBeforeUnload';
import { InfoMessage } from 'ui/shared/messages';

require('./DeviceScheduleForm.css');

export type Props = {
    buildingId: BuildingId;
    canEdit: boolean;
    infotainmentOrg: BuildingOrg | null;
    deviceType: DeviceType;
    isPI: boolean;
    saving: boolean;
    schedule: Schedule;
    onSave(s: Schedule): void;
};

function makeNewRule() {
    return {
        themeId: null,
    } as unknown as ScheduleRule; // null is not allowed in themeId... but we will filter out invalid rules in handleSave.
}

export const DeviceScheduleForm = ({ buildingId, canEdit, infotainmentOrg, deviceType, isPI, saving, schedule, onSave }: Props) => {
    const { t } = useTranslation();

    const [ values, setValues ] = useState(schedule);

    const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setValues(values => {
            onSave({
                ...values,
                // Remove rules where 'No Theme' was selected (i.e. those with a null ID)
                rules: values.rules.filter(r => !!r.themeId),
            });
            return values;
        });
        return false;
    }, [ onSave, setValues ]);

    const handleAdd = useCallback(() => {
        setValues(v => ({
            ...v,
            rules: [
                ...v.rules,
                makeNewRule(),
            ],
        }));
    }, [ setValues ]);

    const handleRemove = useCallback((n: number) => {
        setValues(v => ({
            ...v,
            rules: v.rules.filter((_, i) => i !== n),
        }));
    }, [ setValues ]);

    const handleChange = useCallback((n: number, rule: Partial<ScheduleRule>) => {
        setValues(v => ({
            ...v,
            rules: v.rules.map((r, i) => i === n ? { ...r, ...rule } : r),
        }));
    }, [ setValues ]);

    const handleUndo = useCallback(() => {
        setValues(schedule);
    }, [ setValues ]);

    // Internalize the input schedule when it changes
    // M1-1165: HACK: some re-renders provide a new schedule object even when the data within
    // hasn't changed. So we're using the stringified value of the schedule to determine when
    // it is new data and should be imported.
    const scheduleJson = JSON.stringify(schedule);
    useEffect(() => {
        setValues(JSON.parse(scheduleJson));
    }, [ scheduleJson ]);

    // If the schedule has no rules, then automatically add a default rule
    if (!values.rules.length) {
        handleAdd();
    }

    const noDefaultTheme = !values.rules.length || !values.rules[0].themeId;
    const isDirty = !isEqual(schedule, values);
    
    useBeforeUnload(isDirty);

    return (
        <form
            id="device-schedule-form"
            onSubmit={ handleSubmit }
            className={ `ui form ${saving ? 'loading' : ''}` }
        >
            { infotainmentOrg &&
                <>
                    <InfoMessage message={ t('BUILDING.THEMES_MANAGED_BY', [ infotainmentOrg.displayName ]) }/>
                    <span className="_vc-info">
                        <a href="https://www.verticalcity.com/register">Register / Opt Out</a> or call <a href="tel:18667893776">1-866-789-3776</a>
                    </span>
                </>
            }
            <div>
                { values.rules.map((rule, i) =>
                    rule && <ScheduleRulePanel
                        disabled={ !canEdit }
                        key={ i }
                        index={ i }
                        buildingId={ buildingId }
                        deviceType={ deviceType }
                        isPI={ isPI }
                        scheduleRule={ rule }
                        onChange={ r => handleChange(i, r as ScheduleRule) }
                        onRemoveRule={ () => handleRemove(i) }
                    />
                ) }
                { !noDefaultTheme &&
                    <button type="button" disabled={ !canEdit } className="ui basic button" onClick={ handleAdd } style={ { marginBottom: '2em' } }>
                        { t('COMMAND.ADD_RULE') }
                    </button>
                }
            </div>
            <div>
                <button type="submit" className="ui primary submit button" disabled={ saving || noDefaultTheme || !isDirty || !canEdit }>
                    { t('COMMAND.SAVE') }
                </button>
                <button type="button" className="ui button" disabled={ saving || !isDirty || !canEdit } onClick={ handleUndo }>
                    { t('COMMAND.UNDO') }
                </button>
            </div>
        </form>
    );
};
